/**
 * detection.ts — pure math helpers for the "who can see whom" subsystem.
 *
 * The detection model is deterministic and binary:
 *
 *   - Vision ring: inside the effective vision radius and NOT cloaked ⇒ the
 *     target is fully visible (rendered with its real icon). Stealth does
 *     not reduce vision — vision is short-range, in-the-same-room sight.
 *   - Scan ring: inside the effective scan radius, NOT cloaked, and
 *     scanner.detectionEffective > target.stealthEffective ⇒ the target is
 *     visible as a blip (rendered as a dot, no ship-type reveal).
 *   - Cloak hard-gates both channels — a cloaked ship is invisible
 *     regardless of range or scanner strength.
 *
 * Stealth still matters against scan: raise Stealth above an enemy's
 * Detection and their scanners don't pick you up at all (vision still does
 * if they get close). Firing drops effective Stealth to 0 for a window
 * (see `effectiveStealth`), so a ship that opens fire lights up on any
 * positive-Detection scanner that covers it.
 *
 * These helpers have no state mutation, no RNG, and no time source — every
 * input is passed explicitly so tests (and the dev overlay) can exercise
 * each formula in isolation. The *shape* of each check is locked by
 * MECHANICS §3; the *numbers* feeding them come from `balance-detection.ts`
 * so the whole sensor envelope can be tuned without touching this file.
 *
 * The full detection pipeline (walking ships, bumping VisibilityRecords,
 * expiring grace windows, etc.) lives in `advance-detection.ts` and calls
 * into these helpers.
 */

import {
  DEFAULT_DETECTION_BALANCE,
  type DetectionBalance,
} from './balance-detection';

/**
 * A scanner ship's view of the world — everything the vision/scan checks need
 * about the *looking* side. Packaged as a small struct so call sites aren't
 * threading seven positional arguments.
 */
export interface ScannerView {
  /** In-system sublight position (local 0..1 coords). */
  sx: number;
  sy: number;
  /** Authored per-template vision range (before VISION_RANGE_SCALE). */
  visionRange: number;
  /** Authored per-template scan range (before SCAN_RANGE_SCALE). */
  scanRange: number;
  /** Effective Detection at *now* — call `effectiveDetection` to compute. */
  detectionEffective: number;
}

/**
 * A target ship's view — everything the checks need about the *hiding* side.
 * Cloak is a hard-gate flag, so it's a dedicated field rather than folded into
 * `stealthEffective` (see `visionSees` / `scanSees` early returns).
 */
export interface TargetView {
  sx: number;
  sy: number;
  /** Is the cloak currently active this tick? Cloak unconditionally hides. */
  cloaked: boolean;
  /** Effective Stealth at *now* — call `effectiveStealth` to compute. */
  stealthEffective: number;
}

/**
 * Effective Stealth for a ship at the given tick.
 *
 * Drops to 0 for `FIRING_BREAKS_STEALTH_TICKS` after the ship last fired any
 * weapon (MECHANICS §2.1). `lastFiredTick === null` means "never fired"; any
 * real tick value is compared against `now` against the balance window.
 *
 * Combat isn't wired yet, so `lastFiredTick` stays null for every ship in v1
 * and this reduces to `stealthBase`. The hook is already live so the moment
 * firing lands, stealth-suppression-on-fire starts working without a second
 * touch here.
 *
 * Note: vision is stealth-agnostic under the binary model, so this only
 * affects the Scan channel's Detection > Stealth threshold. A ship that
 * fires and has stealth knocked to 0 becomes visible to any scanner with
 * any positive Detection that covers it in range.
 */
export function effectiveStealth(
  stealthBase: number,
  lastFiredTick: number | null,
  now: number,
  balance: DetectionBalance = DEFAULT_DETECTION_BALANCE,
): number {
  if (lastFiredTick !== null && now - lastFiredTick < balance.firingBreaksStealthTicks) {
    return 0;
  }
  return stealthBase;
}

/**
 * Effective Detection for a ship.
 *
 * A pass-through today — there are no Detection modifiers in v1 — but declared
 * so callers don't sprinkle `tmpl.detectionBase` directly through advance.ts.
 * When structures / buffs / racials arrive, the whole pipeline picks them up
 * by editing this helper alone.
 */
export function effectiveDetection(
  detectionBase: number,
  _balance: DetectionBalance = DEFAULT_DETECTION_BALANCE,
): number {
  return detectionBase;
}

/**
 * Vision-ring visibility check (binary):
 *
 *   visible ⇔  !cloaked  AND  dist(scanner, target) < visionRangeEffective
 *
 * Vision is a short-range "in the same room" reveal — when it triggers, the
 * target is rendered with its real icon and identity. Stealth does NOT
 * suppress vision; only cloak can hide a ship that's inside an enemy's
 * vision ring. Effective range is `authored × VISION_RANGE_SCALE`.
 */
export function visionSees(
  scanner: ScannerView,
  target: TargetView,
  balance: DetectionBalance = DEFAULT_DETECTION_BALANCE,
): boolean {
  if (target.cloaked) return false;
  const range = scanner.visionRange * balance.visionRangeScale;
  if (!(range > 0)) return false;
  const dx = target.sx - scanner.sx;
  const dy = target.sy - scanner.sy;
  return dx * dx + dy * dy < range * range;
}

/**
 * Scan-ring visibility check (binary):
 *
 *   visible ⇔  !cloaked
 *              AND  scanner.detectionEffective > target.stealthEffective
 *              AND  dist(scanner, target) ≤ scanRangeEffective
 *
 * Scan is the long-range radar channel — when it triggers the target is
 * rendered as a contact blip (dot), without its icon or full identity. A
 * target whose Stealth is ≥ the scanner's Detection is invisible to scan
 * even inside the ring; only vision (or cloak drop) will reveal it. Range
 * is `authored × SCAN_RANGE_SCALE`.
 */
export function scanSees(
  scanner: ScannerView,
  target: TargetView,
  balance: DetectionBalance = DEFAULT_DETECTION_BALANCE,
): boolean {
  if (target.cloaked) return false;
  if (scanner.detectionEffective <= target.stealthEffective) return false;
  const range = scanner.scanRange * balance.scanRangeScale;
  if (!(range > 0)) return false;
  const dx = target.sx - scanner.sx;
  const dy = target.sy - scanner.sy;
  return dx * dx + dy * dy <= range * range;
}
